import { useWalletContext } from 'hooks/context/useWalletContext'
import { CustomerWallet } from 'types/customers'
import { ExportConfig } from './types'
import { useOnceOffTransactionExportBase } from './useOnceOffTransactionExportBase'

interface UseOnceOffWalletTransactionsExportProps {
  handleClose: () => void
  downloadAll: boolean
  customerWallet?: CustomerWallet
}

/**
 * Wallet-specific transaction export hook
 * Provides export functionality for wallet transactions with full feature support
 *
 * Features:
 * - Single wallet or all wallets export
 * - Transaction and balance reports
 *
 * @param props - Configuration for wallet export
 * @returns Transaction export hook result with all necessary state and handlers
 *
 * @example
 * // Single wallet export
 * const exportValues = useOnceOffWalletTransactionsExport({
 *   handleClose: () => setModalOpen(false),
 *   downloadAll: false,
 *   customerWallet: undefined
 * })
 *
 * @example
 * // All wallets export
 * const exportValues = useOnceOffWalletTransactionsExport({
 *   handleClose: () => setModalOpen(false),
 *   downloadAll: true,
 *   customerWallet: undefined
 * })
 */
export const useOnceOffWalletTransactionsExport = ({
  handleClose,
  downloadAll,
  customerWallet,
}: UseOnceOffWalletTransactionsExportProps) => {
  const wallet = useWalletContext()

  const config: ExportConfig = {
    walletIdResolver: () => ({
      id: customerWallet?.id || wallet?.id,
      lsid: customerWallet?.id || wallet?.lsid,
    }),
    buildPayloadExtras: formValues => ({
      balanceDate: formValues?.balanceDate,
    }),
    amplitudePropsExtras: (_, walletIds) => ({
      ...(!downloadAll && { walletId: walletIds.id }),
    }),
    features: {
      allowBalanceReport: true, // Supports both transaction and balance reports
    },
    // Error context for Sentry
    errorContext: 'WalletTransactionExport',
  }

  return useOnceOffTransactionExportBase(config, handleClose)
}
